import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import moment from "moment";

import ConfirmationPopup from "../../../packages/ConfirmationPopup";
import CustomModal from "../../../packages/CustomModal";
import WPDropdown from "../../../packages/WPDropdown";
import WPMultiSelectCheckboxDropdown from "../../../packages/WPMultiSelectCheckboxDropdown";

import IconWithTooltip from "../../../common/styles/components/headings/IconWithTooltip";

import { ValueStrings } from "../../../utils/constantsString";

import "./index.scss";

const logImportDebug = (...args) => {
  console.log("[ImportTrainConfig]", ...args);
};

const getTrainNumber = (trainLabel) =>
  Number(trainLabel?.match(/(\d+)$/)?.[1]) || null;
const getTrainPrefix = (trainLabel) =>
  trainLabel?.match(/^(.+?)\s*\d+$/)?.[1]?.trim() || null;

const formatSelectedTrainsDisplay = (trainLabels) => {
  if (!trainLabels?.length) return "";
  if (trainLabels.length === 1) return trainLabels[0];

  const commonPrefix = getTrainPrefix(trainLabels[0]);
  const allSharePrefix =
    commonPrefix &&
    trainLabels.every((label) => getTrainPrefix(label) === commonPrefix);
  if (!allSharePrefix) return trainLabels.join(", ");

  const trainNumbers = trainLabels
    .map(getTrainNumber)
    .filter(Boolean)
    .sort((a, b) => a - b);
  return trainNumbers.length === trainLabels.length
    ? `${commonPrefix} ${trainNumbers.join(", ")}`
    : trainLabels.join(", ");
};

const ImportTrainConfigModal = ({
  show,
  trains,
  onClose,
  onImport,
  isTrainConfigured,
}) => {
  const { t } = useTranslation();

  const [sourceTrainOption, setSourceTrainOption] = useState(null);
  const [targetTrainOptions, setTargetTrainOptions] = useState([]);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const prevSourceTrainIdRef = useRef(undefined);
  const prevTargetsKeyRef = useRef("");

  const trainList = useMemo(
    () => Object.values(trains || {}).sort((a, b) => a.train_id - b.train_id),
    [trains],
  );

  const importFromOptions = useMemo(
    () =>
      trainList.filter(isTrainConfigured).map((train) => ({
        label: `${t("ValueStrings.train")} ${train.train_id}`,
        value: train.train_id,
      })),
    [trainList, isTrainConfigured, t],
  );

  const sourceTrainId = sourceTrainOption?.value ?? null;

  const sourceTrain = useMemo(
    () => trainList.find((train) => train.train_id === sourceTrainId),
    [trainList, sourceTrainId],
  );

  const importToOptions = useMemo(
    () =>
      trainList
        .filter((train) => train.train_id !== sourceTrainId)
        .map((train) => ({
          label: `${t("ValueStrings.train")} ${train.train_id}`,
          value: train.train_id,
        })),
    [sourceTrainId, t, trainList],
  );

  const sourceTrainTotalElements = useMemo(() => {
    if (!sourceTrain?.passes) return 0;
    return sourceTrain.passes.reduce(
      (total, pass) =>
        total +
        (pass.stages || []).reduce(
          (sum, stage) =>
            sum +
            Number(stage?.num_pressure_vessels) *
              Number(stage?.num_element_per_pressure_vessel),
          0,
        ),
      0,
    );
  }, [sourceTrain]);

  const totalElements = sourceTrainTotalElements * targetTrainOptions.length;

  const passSummaries = useMemo(() => {
    if (!sourceTrain?.passes) return [];
    return sourceTrain.passes.map((pass) => ({
      passId: pass.pass_id,
      stages: (pass.stages || []).map((stage) => ({
        stageId: stage.stage_id,
        numPressureVessels: stage.num_pressure_vessels,
        numElementsPerPressureVessel: stage.num_element_per_pressure_vessel,
        installationDate: stage.installation_year,
        isIsd: stage.is_isd,
        elementsUsed: Array.isArray(stage.ro_nf_element_used)
          ? stage.ro_nf_element_used
          : stage.ro_nf_element_used
            ? [stage.ro_nf_element_used]
            : [],
      })),
    }));
  }, [sourceTrain]);

  useEffect(() => {
    if (!show || importFromOptions.length === 0) return;
    setSourceTrainOption((prev) => {
      const nextSourceOption =
        importFromOptions.find((opt) => opt.value === prev?.value) ||
        importFromOptions[0];
      if (prev?.value !== nextSourceOption?.value) {
        logImportDebug(`Source train set to ${nextSourceOption?.value ?? "N/A"}`);
      }
      return nextSourceOption;
    });
  }, [show, importFromOptions]);

  useEffect(() => {
    if (prevSourceTrainIdRef.current === sourceTrainId) return;
    logImportDebug(`Source changed to ${sourceTrainId}, cleared target trains`);
    prevSourceTrainIdRef.current = sourceTrainId;
    setTargetTrainOptions([]);
  }, [sourceTrainId]);

  useEffect(() => {
    const targetTrainIds = targetTrainOptions.map((opt) => opt.value);
    const targetsKey = `${sourceTrainId ?? ""}|${targetTrainIds.join(",")}|${totalElements}`;
    if (prevTargetsKeyRef.current === targetsKey) return;
    prevTargetsKeyRef.current = targetsKey;
    logImportDebug(
      `Targets selected: [${targetTrainIds.join(", ")}], total elements: ${totalElements}`,
    );
  }, [
    sourceTrainId,
    targetTrainOptions,
    totalElements,
  ]);

  const handleModalClose = useCallback(
    ({ target }) => {
      if (target?.id === "modal-confirm-btn") {
        setShowWarningDialog(true);
      } else {
        onClose();
      }
    },
    [onClose],
  );

  const handleWarningDialogClose = useCallback(
    (buttonId) => {
      setShowWarningDialog(false);
      if (buttonId === "modal-confirm-btn") {
        const targetTrainIds = targetTrainOptions.map((opt) => opt.value);
        logImportDebug(
          `Import confirmed. Source: ${sourceTrainId}, Targets: [${targetTrainIds.join(", ")}]`,
        );
        onImport(sourceTrainId, targetTrainIds);
        onClose();
      }
    },
    [
      onClose,
      onImport,
      sourceTrainId,
      targetTrainOptions,
    ],
  );

  if (!show) return null;

  return (
    <CustomModal
      header={t("importTrainConfig.modalTitle")}
      cancelBtn={t("ValueStrings.cancel")}
      confirmBtn={t("importTrainConfig.importConfig")}
      confirmBtnDisabled={targetTrainOptions.length === 0}
      onModalClose={handleModalClose}
      width="800px"
      custombodyclass="import-train-config-modal-body"
    >
      <div className="import-train-config-top">
        <div className="import-train-config-control import-train-config-control-from">
          <span className="import-train-config-label">
            {t("importTrainConfig.importFrom")}
          </span>
          <WPDropdown
            id="import-train-config-from"
            value={sourceTrainOption}
            options={importFromOptions}
            onChange={(selectedOption) =>
              setSourceTrainOption(selectedOption || null)
            }
            customClassName="import-train-config-dropdown import-train-config-dropdown-from"
          />
        </div>

        <div className="import-train-config-divider" />

        <div className="import-train-config-control import-train-config-control-to">
          <span className="import-train-config-label">
            {t("importTrainConfig.importTo")}
          </span>
          <WPMultiSelectCheckboxDropdown
            id="import-train-config-to"
            value={targetTrainOptions}
            options={importToOptions}
            onChange={(selectedOptions) =>
              setTargetTrainOptions(selectedOptions || [])
            }
            customClassName="import-train-config-dropdown import-train-config-dropdown-to"
            placeholder={t("importTrainConfig.selectTrains")}
            showSelectAll
            formatSelectedDisplay={formatSelectedTrainsDisplay}
          />
        </div>

        <div className="import-train-config-divider" />

        <div className="import-train-config-total">
          <span className="import-train-config-label">
            {t("importTrainConfig.total")}
          </span>
          <span className="import-train-config-total-value">
            {totalElements}
          </span>
          <span className="import-train-config-total-text">
            {t("addNewPlant.elements")}
          </span>
        </div>
      </div>

      <div className="import-train-config-table">
        <div className="import-train-config-header-row">
          <span>{t("addNewPlant.pass")}</span>
          <span>{t("addNewPlant.stage")}</span>
          <span>{t("importTrainConfig.noOfPVs")}</span>
          <span>{t("importTrainConfig.noOfElsPerPVs")}</span>
          <span>{t("importTrainConfig.installDate")}</span>
          <span>{t("importTrainConfig.roNfElementsHeader")}</span>
        </div>

        {passSummaries.map((passSummary) => (
          <div
            key={passSummary.passId}
            className="import-train-config-pass-block"
          >
            <div className="import-train-config-pass-row">
              {`${t("addNewPlant.pass")} ${passSummary.passId}`}
            </div>

            {passSummary.stages.map((stageSummary) => {
              const mode = stageSummary.isIsd
                ? t("importTrainConfig.isd")
                : t("addNewPlant.single");
              const elementsUsedTooltip = stageSummary.elementsUsed.join(", ");
              const elementsLabel = t("importTrainConfig.elementsPerMode", {
                count: Number(stageSummary.numElementsPerPressureVessel || 0),
                mode,
              });

              return (
                <div
                  key={`${passSummary.passId}-${stageSummary.stageId}`}
                  className="import-train-config-data-row"
                >
                  <span />
                  <span>{stageSummary.stageId}</span>
                  <span className="import-train-config-pill">
                    {stageSummary.numPressureVessels}
                  </span>
                  <span className="import-train-config-pill">
                    {stageSummary.numElementsPerPressureVessel}
                  </span>
                  <span>
                    {stageSummary.installationDate &&
                      moment(stageSummary.installationDate).format(
                        ValueStrings.dateFormatMonthAbbr,
                      )}
                  </span>
                  <span className="import-train-config-elements-cell">
                    <span className="import-train-config-elements-value">
                      <IconWithTooltip
                        label={elementsUsedTooltip}
                        placement="bottom"
                      />
                      <span>{elementsLabel}</span>
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {showWarningDialog && (
        <ConfirmationPopup
          type="warning"
          header={t("importTrainConfig.resetTitle")}
          description={t("importTrainConfig.resetDescription")}
          onClose={handleWarningDialogClose}
          cancelBtn={t("ValueStrings.cancel")}
          confirmBtn={t("importTrainConfig.yesImport")}
        />
      )}
    </CustomModal>
  );
};

export default ImportTrainConfigModal;
